import React from 'react';
import { useTranslation } from 'react-i18next';
import { Inbox } from 'lucide-react';

/**
 * Ton pustego stanu (Figma „08.5 · Data table" — Empty: 3 warianty):
 *  - `neutral` — brak danych (kółko `surface/subtle`, ikona `text/muted`),
 *  - `search`  — brak wyników wyszukiwania/filtrów (kółko brand `accent/50`, ikona `accent/600`),
 *  - `error`   — błąd ładowania (kółko `error/10%`, ikona `error/base`); ton błędu jest dodatkowo
 *    ogłaszany czytnikom ekranu (`role="alert"`) — używają go `errorState` i `loadError` tabeli
 *    (`ConfigurableTable`, jedna wspólna ścieżka renderu), pozostałe tony bez zmian.
 */
export type TableEmptyTone = 'neutral' | 'search' | 'error';

interface TableEmptyProps {
  icon?: React.ComponentType<{ className?: string }>;
  /** Tytuł; bez propa — domyślny „Brak danych" (`ui.configurableTable.emptyTitle`). */
  title?: string;
  /**
   * Opis pod tytułem; bez propa — domyślny generyczny tekst (`ui.configurableTable.emptyDescription`).
   * Pusty string = bez akapitu opisu (gdy tytuł mówi wszystko).
   */
  description?: string;
  action?: React.ReactNode;
  /** Ton wizualny kółka + ikony (domyślnie neutralny). */
  tone?: TableEmptyTone;
}

const TONE_CLASSES: Record<TableEmptyTone, { circle: string; icon: string }> = {
  neutral: { circle: 'bg-bg-tertiary', icon: 'text-text-muted' },
  search: { circle: 'bg-accent-50', icon: 'text-accent-600' },
  error: {
    circle: 'bg-[rgb(var(--color-error)/0.1)]',
    icon: 'text-[rgb(var(--color-error))]',
  },
};

export function TableEmpty({
  icon: Icon = Inbox,
  title,
  description,
  action,
  tone = 'neutral',
}: TableEmptyProps) {
  const { t } = useTranslation();
  const toneCls = TONE_CLASSES[tone];
  const resolvedTitle = title ?? t('ui.configurableTable.emptyTitle');
  const resolvedDescription = description ?? t('ui.configurableTable.emptyDescription');
  return (
    <div
      className="flex flex-col items-center justify-center py-12 px-4"
      role={tone === 'error' ? 'alert' : undefined}
    >
      <div
        className={`w-16 h-16 rounded-full ${toneCls.circle} flex items-center justify-center mb-4`}
      >
        {/* Ikona dekoracyjna — znaczenie niesie tytuł; czytnik ekranu ma ją pominąć. Owinięta,
            bo `icon` bywa dowolnym komponentem (nie każdy przekazuje `aria-hidden` dalej). */}
        <span aria-hidden="true" className="flex">
          <Icon className={`w-8 h-8 ${toneCls.icon}`} />
        </span>
      </div>
      {/* `??` (nie `||`): pusty napis od wołającego (`emptyDescription=""`) ma zostać pusty —
          a pusty opis nie rysuje w ogóle akapitu. */}
      <h3 className="text-lg font-medium text-text-primary mb-1">{resolvedTitle}</h3>
      {resolvedDescription !== '' && (
        <p className="text-sm text-text-muted text-center max-w-sm mb-4">{resolvedDescription}</p>
      )}
      {/* Bez opisu odstęp tytuł→akcja jak dotąd: dawny pusty akapit dokładał swoje `mb-4`
          (w kolumnie flex marginesy się nie zlewają), więc akcja dostaje `mt-4`. */}
      {action && <div className={resolvedDescription === '' ? 'mt-4' : undefined}>{action}</div>}
    </div>
  );
}
